import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTable, useSortBy, useGlobalFilter } from "react-table";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import GlobalFilter from "../components/common/filter";
import { url } from "../constants/url";

const Salons = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSalons = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(url + "/api/admin/salons", {
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : ""
        }
      });
      if (!response.ok) throw new Error("Failed to fetch salons");

      const salons = await response.json();
      const mappedData = salons.map((salon) => {
        const createdAt = salon.UserInformation?.createdAt;
        return {
          id: salon.id,
          salonName: salon.fullName || salon.name || "N/A",
          location: salon.UserInformation?.city || "N/A",
          joiningDate: createdAt
            ? new Date(createdAt).toLocaleDateString()
            : "N/A",
          joiningDateRaw: createdAt ? new Date(createdAt).getTime() : 0,
          hasSubscription: Array.isArray(salon.Subscription)
            ? salon.Subscription.length > 0
            : Boolean(salon.Subscription),
          isSuspended: salon.isSuspended || false
        };
      });

      setData(mappedData);
    } catch (err) {
      console.error("Error fetching salons:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSalons();
  }, [fetchSalons]);

  const navigate = useNavigate();

  const handleSuspend = useCallback(async (id) => {
    if (!window.confirm("Are you sure you want to suspend this salon?")) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${url}/api/admin/salons/${id}/suspend`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });

      // 401 → clear auth and redirect
      if (response.status === 401) {
        localStorage.clear();
        navigate("/login");
        return;
      }
      if (!response.ok) throw new Error("Failed to suspend salon");

      setData((prev) =>
        prev.map((salon) =>
          salon.id === id ? { ...salon, isSuspended: true } : salon
        )
      );
    } catch (err) {
      console.error("Error suspending salon:", err);
    }
  }, []);

  const handleUnsuspend = useCallback(async (id) => {
    if (!window.confirm("Are you sure you want to unsuspend this salon?"))
      return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${url}/api/admin/salons/${id}/unsuspend`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });

      // 401 → clear auth and redirect
      if (response.status === 401) {
        localStorage.clear();
        navigate("/login");
        return;
      }
      if (!response.ok) throw new Error("Failed to unsuspend salon");

      setData((prev) =>
        prev.map((salon) =>
          salon.id === id ? { ...salon, isSuspended: false } : salon
        )
      );
    } catch (err) {
      console.error("Error unsuspending salon:", err);
    }
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm("Are you sure you want to delete this salon?")) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${url}/api/admin/users/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });

      // 401 → clear auth and redirect
      if (response.status === 401) {
        localStorage.clear();
        navigate("/login");
        return;
      }
      if (!response.ok) throw new Error("Failed to delete salon");

      setData((prev) => prev.filter((salon) => salon.id !== id));
    } catch (err) {
      console.error("Error deleting salon:", err);
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: "Salon Name",
        accessor: "salonName",
        Cell: ({ row, value }) => (
          <Link
            to={`/salons/${row.original.id}`}
            className="text-blue-500 no-underline"
          >
            {value}
          </Link>
        )
      },
      { Header: "Location", accessor: "location" },
      {
        Header: "Joining Date",
        accessor: "joiningDateRaw",
        Cell: ({ row }) => row.original.joiningDate
      },
      {
        Header: "Subscription",
        accessor: "hasSubscription",
        Cell: ({ value }) => (value ? "Yes" : "No")
      },
      {
        Header: "Action",
        Cell: ({ row }) => (
          <button
            onClick={() =>
              row.original.isSuspended
                ? handleUnsuspend(row.original.id)
                : handleSuspend(row.original.id)
            }
            className="text-blue-500 hover:underline"
          >
            {row.original.isSuspended ? "Unsuspend" : "Suspend"}
          </button>
        )
      },
      {
        Header: "Delete",
        Cell: ({ row }) => (
          <button
            onClick={() => handleDelete(row.original.id)}
            className="text-red-500 hover:underline"
          >
            Delete
          </button>
        )
      }
    ],
    [handleSuspend, handleUnsuspend, handleDelete]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
    preGlobalFilteredRows
  } = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [{ id: "joiningDateRaw", desc: true }]
      }
    },
    useGlobalFilter,
    useSortBy
  );

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-3xl mb-4">Salons</h1>
        <div className="animate-pulse space-y-2">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="h-8 bg-gray-300 rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-red-500">Error: {error.message}</div>;
  }

  return (
    <div className="p-6 bg-white rounded-3xl shadow-md">
      <h1 className="text-3xl mb-4">Salons</h1>
      <GlobalFilter
        preGlobalFilteredRows={preGlobalFilteredRows}
        globalFilter={state.globalFilter}
        setGlobalFilter={setGlobalFilter}
      />
      <div className="overflow-x-auto mt-4">
        <table {...getTableProps()} className="min-w-full">
          <thead>
            {headerGroups.map((hg) => (
              <tr {...hg.getHeaderGroupProps()}>
                {hg.headers.map((col) => (
                  <th
                    {...col.getHeaderProps(col.getSortByToggleProps())}
                    className="px-4 py-2 border-b text-left"
                  >
                    <div className="flex items-center">
                      {col.render("Header")}
                      <span className="ml-1">
                        {col.isSorted ? (
                          col.isSortedDesc ? (
                            <FaSortDown />
                          ) : (
                            <FaSortUp />
                          )
                        ) : (
                          <FaSort />
                        )}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} className="hover:bg-gray-50">
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()} className="px-4 py-2 border-b">
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Salons;
